import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  FeedbackItem,
  FeedbackStatusRequest,
  FeedbackSubmitRequest,
} from '@internal/plugin-techdocs-feedback-common';
import type { TechDocsFeedbackApi } from './TechDocsFeedbackApi';

export class FeedbackApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'FeedbackApiError';
  }
}

const getErrorMessage = async (response: Response): Promise<string> => {
  const responseText = await response.text();
  if (!responseText) return `Request failed with status ${response.status}`;
  try {
    const parsed = JSON.parse(responseText);
    if (typeof parsed?.error === 'string') return parsed.error;
    if (typeof parsed?.error?.message === 'string') return parsed.error.message;
  } catch {
    return responseText;
  }
  return responseText;
};

export class TechDocsFeedbackClient implements TechDocsFeedbackApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  private async url(path: string): Promise<string> {
    return `${await this.#discoveryApi.getBaseUrl('techdocs-feedback')}${path}`;
  }

  private async check(response: Response): Promise<Response> {
    if (!response.ok) {
      throw new FeedbackApiError(
        await getErrorMessage(response),
        response.status,
      );
    }
    return response;
  }

  async submit(
    input: FeedbackSubmitRequest,
  ): Promise<{ id: string; status: 'ok' }> {
    const response = await this.#fetchApi.fetch(await this.url('/submit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return (await this.check(response)).json();
  }

  async list(): Promise<FeedbackItem[]> {
    const response = await this.#fetchApi.fetch(await this.url('/feedback'), {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = (await (await this.check(response)).json()) as {
      items?: FeedbackItem[];
    };
    return data.items ?? [];
  }

  async updateStatus(
    id: string,
    input: FeedbackStatusRequest,
  ): Promise<FeedbackItem> {
    const response = await this.#fetchApi.fetch(
      await this.url(`/feedback/${encodeURIComponent(id)}`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    const data = (await (await this.check(response)).json()) as {
      item: FeedbackItem;
    };
    return data.item;
  }
}
